// Basic geometry helpers for zone polygons.
// All helpers operate on {lat, lng} objects; upstream callers should normalize first.

export const normalizePoint = (pt) => {
  if (!pt) return null;
  if (Array.isArray(pt) && pt.length >= 2) {
    const lat = Number(pt[0]);
    const lng = Number(pt[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }
  const lat = Number(pt.lat ?? pt.latitude);
  const lng = Number(pt.lng ?? pt.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

export const normalizePolygon = (zoneOrPoints) => {
  const raw = Array.isArray(zoneOrPoints?.polygon)
    ? zoneOrPoints.polygon
    : Array.isArray(zoneOrPoints)
    ? zoneOrPoints
    : [];
  return raw.map(normalizePoint).filter(Boolean);
};

export const polygonArea = (points = []) => {
  if (!Array.isArray(points) || points.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i += 1) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    sum += p1.lng * p2.lat - p2.lng * p1.lat;
  }
  return Math.abs(sum / 2); // planar approximation, sufficient for ordering/sorting
};

export const polygonBounds = (points = []) => {
  if (!Array.isArray(points) || !points.length) return null;
  let north = -Infinity;
  let south = Infinity;
  let east = -Infinity;
  let west = Infinity;
  points.forEach((p) => {
    north = Math.max(north, p.lat);
    south = Math.min(south, p.lat);
    east = Math.max(east, p.lng);
    west = Math.min(west, p.lng);
  });
  return { north, south, east, west };
};

export const pointInPolygon = (point, polygon = []) => {
  if (!point || !Array.isArray(polygon) || polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;
    const intersect =
      (yi > point.lat) !== (yj > point.lat) &&
      point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

export const centroid = (points = []) => {
  if (!Array.isArray(points) || !points.length) return null;
  const sum = points.reduce(
    (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
    { lat: 0, lng: 0 }
  );
  return { lat: sum.lat / points.length, lng: sum.lng / points.length };
};

const metersToLat = (meters) => meters / 111320;
const metersToLng = (meters, lat) => {
  const denom = 111320 * Math.cos((lat * Math.PI) / 180) || 1;
  return meters / denom;
};

export const seedShapePoints = (center, shape = "square", sizeMeters = 60) => {
  if (!center || typeof center.lat !== "number" || typeof center.lng !== "number") return [];
  const lat = center.lat;
  const lng = center.lng;
  const dLat = metersToLat(sizeMeters);
  const dLng = metersToLng(sizeMeters, lat);
  switch (shape) {
    case "circle": {
      const segments = 16;
      const rLat = metersToLat(sizeMeters);
      const rLng = metersToLng(sizeMeters, lat);
      const pts = [];
      for (let i = 0; i < segments; i += 1) {
        const theta = (2 * Math.PI * i) / segments;
        pts.push({
          lat: lat + Math.sin(theta) * rLat,
          lng: lng + Math.cos(theta) * rLng,
        });
      }
      return pts;
    }
    case "triangle":
      return [
        { lat: lat + dLat, lng },
        { lat: lat - dLat, lng: lng - dLng },
        { lat: lat - dLat, lng: lng + dLng },
      ];
    case "square":
    default:
      return [
        { lat: lat + dLat, lng: lng + dLng },
        { lat: lat + dLat, lng: lng - dLng },
        { lat: lat - dLat, lng: lng - dLng },
        { lat: lat - dLat, lng: lng + dLng },
      ];
  }
};

export const estimateChildSeedSizeMeters = (
  parentPolygon = [],
  areaRatio = 0.5,
  fallbackSizeMeters = 60
) => {
  const points = normalizePolygon(parentPolygon);
  if (points.length < 3) return fallbackSizeMeters;

  const bounds = polygonBounds(points);
  if (!bounds) return fallbackSizeMeters;

  const avgLat = (bounds.north + bounds.south) / 2;
  const latSpanMeters = Math.abs(bounds.north - bounds.south) * 111320;
  const lngSpanMeters =
    Math.abs(bounds.east - bounds.west) *
    (111320 * Math.max(Math.cos((avgLat * Math.PI) / 180), 0.1));
  const minSpanMeters = Math.max(Math.min(latSpanMeters, lngSpanMeters), 20);

  // seedShapePoints treats sizeMeters as half-width (square) / radius (circle),
  // so derive a side length from ratio and then convert to half-width.
  const childSideMeters = Math.sqrt(Math.max(areaRatio, 0.05)) * minSpanMeters;
  const seedSizeMeters = childSideMeters / 2;

  return Math.max(20, Math.min(seedSizeMeters, 5000));
};

export const estimatePlacementZoomForSeedSize = (
  lat,
  sizeMeters = 60,
  targetPixels = 220,
  minZoom = 13,
  maxZoom = 18
) => {
  const numericLat = Number(lat);
  const numericSize = Number(sizeMeters);
  if (!Number.isFinite(numericLat) || !Number.isFinite(numericSize) || numericSize <= 0) {
    return 16;
  }
  const clampedTargetPixels = Math.max(Number(targetPixels) || 220, 60);
  const fullShapeMeters = Math.max(numericSize * 2, 40);
  const metersPerPixel = fullShapeMeters / clampedTargetPixels;
  if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) return 16;

  const metersPerPixelAtZoom0 = 156543.03392 * Math.max(Math.cos((numericLat * Math.PI) / 180), 0.1);
  const rawZoom = Math.log2(metersPerPixelAtZoom0 / metersPerPixel);
  if (!Number.isFinite(rawZoom)) return 16;

  const min = Number.isFinite(Number(minZoom)) ? Number(minZoom) : 13;
  const max = Number.isFinite(Number(maxZoom)) ? Number(maxZoom) : 18;
  return Math.max(min, Math.min(max, rawZoom));
};
